/**
 * <location-map> — loads the Google embed only when it is actually approached.
 *
 * loading="lazy" alone was not enough here. Chrome's lazy threshold is
 * generous and the cafe pages are short, so the embed fetched on load anyway:
 * measured on an iPhone 13 viewport it cost 18 requests, 418KB and 164ms of
 * LCP on a page most visitors open to read an address and leave. Holding the
 * src back until the map is within 300px of the viewport keeps that cost with
 * the people who scroll far enough to want a map.
 *
 * The iframe keeps loading="lazy" as a second line of defence, and a <noscript>
 * copy in the section renders the plain embed when this never runs. Without JS
 * and without the noscript the address is still plain text on the page and the
 * "Get directions" link still works, so nothing here is load-bearing.
 */
class LocationMap extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('iframe[data-src]');
    if (!this.frame) return;

    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load();
      },
      { rootMargin: '300px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  load() {
    this.observer?.disconnect();
    const src = this.frame?.dataset.src;
    if (!src || this.frame.src) return;
    this.frame.src = src;
    this.frame.removeAttribute('data-src');
  }
}

if (!customElements.get('location-map')) {
  customElements.define('location-map', LocationMap);
}
